import type { ReactElement } from 'react';
import type { RecentSearch } from '../repositories/savedSearchRepository';
import { deduplicateRecentSearches, fuzzyHighlightSegments } from '../utils/recentSearches';

export function RecentSearchClockIcon(): ReactElement {
  return (
    <svg
      className="recent-search-icon"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export interface RecentSearchesDropdownProps {
  /** DOM id of the listbox; option ids are `${id}-${index}` for aria-activedescendant. */
  id?: string;
  searches: RecentSearch[];
  isOpen: boolean;
  highlightedIndex?: number;
  onSelect: (search: RecentSearch) => void;
  maxCount?: number;
  query?: string;
}

export function RecentSearchesDropdown({
  id = 'recent-searches',
  searches,
  isOpen,
  highlightedIndex = -1,
  onSelect,
  maxCount = 5,
  query,
}: RecentSearchesDropdownProps): ReactElement | null {
  const items = deduplicateRecentSearches(searches, maxCount, query);
  if (!isOpen || items.length === 0) return null;

  return (
    <div
      id={id}
      className="recent-searches-dropdown"
      role="listbox"
      aria-label="Recent searches"
      onMouseDown={(e) => {
        // Prevent mousedown on the dropdown from blurring the input before selection
        e.preventDefault();
      }}
    >
      {items.map((search, idx) => {
        const isHighlighted = highlightedIndex === idx;
        const label = search.title || search.description;
        const segments = fuzzyHighlightSegments(label, query);
        return (
          <button
            key={search.search_id || `${search.description_id}-${idx}`}
            id={`${id}-${idx}`}
            type="button"
            role="option"
            aria-selected={isHighlighted}
            className={`recent-search-dropdown-item${isHighlighted ? ' active' : ''}`}
            onClick={() => onSelect(search)}
            title={search.description}
          >
            <RecentSearchClockIcon />
            <span className="recent-search-text">
              {segments.map((seg, sIdx) =>
                seg.matched ? (
                  <strong key={sIdx} className="recent-search-match">{seg.text}</strong>
                ) : (
                  <span key={sIdx}>{seg.text}</span>
                )
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

